/**
 * Générateur — Il détermine la liste des nombres premiers inférieurs à 100.
 * Attendu ID : 153 | Niveau : 4ème | Domaine : Divisibilité (desc 10, O18)
 *
 * ⚠️ Aucun exercice dans le doc prof pour O18 → CRÉATION BANQUE (contenu
 * standard du programme, non ambigu). Plan de cartes validé 2026-07-14, tel
 * qu'il est depuis la revue du 2026-09-23 :
 *
 *   Carte « nature » — Le nombre n est-il premier ?
 *     qcm_unique (Oui / Non : un autre diviseur / Non : un seul diviseur, ce
 *     dernier juste pour n = 1 seulement, phrase du cours) + tableau_vf à
 *     quatre lignes (« n est un nombre premier. »).
 *
 *   Carte « crible » — Sélectionne les k nombres premiers (qcm_multiple)
 *     une liste de six nombres, ou un tirage sur deux les impairs d'un
 *     intervalle de largeur 20 qui ne finissent pas par 5. La consigne annonce
 *     COMBIEN.
 *
 *   Carte « liste » — La liste des premiers < 100
 *     texte_trou_saisie (fenêtre de premiers consécutifs, quelques trous) +
 *     texte (« combien de nombres premiers entre a et b ? », comptage exact).
 *
 * Tout par construction ; primalité et comptage exacts (module premiers.js),
 * revérifiés par la définition du cours (exactement deux diviseurs), throw sinon.
 *
 * Revue 2026-09-23 (vague 153-163), attendu de fin de 4e : « Il détermine la
 * liste des nombres premiers inférieurs à 100. » Exemple de réussite :
 * « Énumère tous les nombres premiers compris entre 50 et 70. » Cours du prof
 * (19Divi-np) : définition « exactement deux diviseurs qui sont 1 et
 * lui-même », liste des 25 premiers, « Le nombre 1 n'est pas premier car il
 * n'a qu'un seul diviseur », « 2 est le seul nombre pair qui est premier ».
 *   - « nature » QCM : le mot « composé » n'est pas dans le cours (il dit
 *     « non premier »), il sort du libellé, de la question et des choix. Trois
 *     choix : « Oui, c'est un nombre premier. », « Non : il a un autre
 *     diviseur que 1 et lui-même. », « Non : il n'a qu'un seul diviseur. »
 *     (juste pour 1 seulement, la phrase du cours). Tirage recadré : 40 % de
 *     premiers de 17 à 97, 40 % de « sosies » (impairs non premiers qui ont
 *     l'air premiers : 21, 27, 33, 39, 49, 51, 57, 63, 69, 77, 81, 87, 91, 93,
 *     99), 10 % le nombre 1, 10 % le nombre 2 ou un autre nombre pair. Avant,
 *     n était uniforme de 2 à 99 et les seuls critères de 2 et de 5 suffisaient
 *     dans 86,0 % des tirages (59,5 % après). Familles nommées et exportées
 *     (REGLES_DISTRACTEURS_QCM) : les quatre des décisions, plus deux noms
 *     ajoutés par le correcteur pour les combinaisons qu'elles ne nommaient
 *     pas (« Oui » pour un pair autre que 2, « autre diviseur » pour 1).
 *   - « nature » Vrai/Faux : quatre lignes au plus (règle prof), de 1 à 3
 *     premiers de 17 à 97, le reste parmi les sosies et 1 ; 4, 6 et 8 (faux
 *     sans réflexion, 23,8 % des tableaux) retirés du vivier.
 *   - « crible » : premiers de 17 à 97 (2 à 13 se reconnaissent sans la
 *     liste) ; un tirage sur deux, la variante de l'exemple de réussite,
 *     « Sélectionne les k nombres premiers compris entre a et a + 20 » (a pair
 *     de 10 à 80), les choix étant les huit impairs de l'intervalle qui ne
 *     finissent pas par 5. Familles nommées et exportées.
 *   - Carte « diviseur » (plus petit diviseur premier) DÉPLACÉE en 154, même
 *     clé : c'est la première étape de la méthode du cours pour décomposer
 *     (« On commence par tester si 300 est divisible par 2 »). Mode auto,
 *     À ARBITRER.
 *   - « liste » trous : la fenêtre commence à 11 au moins ; consigne en deux
 *     phrases, sans parenthèse, et « sans en oublier aucun » ferme le cas du
 *     dernier nombre caché.
 *   - « liste » saisie : bornes multiples de 10 (jamais premières, la
 *     parenthèse « (compris) » n'a plus rien à trancher et sort), largeurs 10,
 *     20 ou 30 ; question « Combien y a-t-il de nombres premiers compris entre
 *     a et b ? », la tournure de l'exemple de réussite.
 *   - Rappel brouillon : AUCUN, par attendu. La connaissance est la liste, et
 *     un nombre inférieur à 100 se teste de tête par 2, 3, 5 et 7 ; les
 *     intervalles de « liste » ne dépassent plus 30.
 *   - Rien à passer en KaTeX : des entiers seuls, ni fraction, ni produit.
 */

import {
    randomChoice, randInt, shuffle, randomChoiceMultiple,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';
import { PREMIERS_100, estPremier } from './premiers';

const ATTENDU_ID = 153;

const L_NATURE = 'Reconnaître un nombre premier';
const L_CRIBLE = 'Repérer les nombres premiers d\'une liste';
const L_LISTE = 'Connaître la liste des nombres premiers inférieurs à 100';

const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Second chemin : la définition du cours ────────────────────────────────
// Nombre de diviseurs par énumération (exactement deux ⟺ premier). Sert à
// revérifier chaque tirage indépendamment de premiers.js ; un désaccord lève.
const nbDiviseurs = (n) => {
    let k = 0;
    for (let d = 1; d <= n; d++) if (n % d === 0) k++;
    return k;
};
const verifiePremier = (n) => {
    const def = nbDiviseurs(n) === 2;
    if (def !== estPremier(n)) throw new Error(`gen_153 : primalité de ${n} incohérente`);
    return def;
};

// Premiers « de la liste » (périmètre : de 17 à 97 ; 2, 3, 5, 7, 11 et 13 se
// reconnaissent sans elle).
const PREMIERS_17_97 = PREMIERS_100.filter((p) => p >= 17);
// Sosies : impairs non premiers, qui ne finissent pas par 5 et qui « ont l'air »
// premiers ; seul le test de 3 ou de 7 les démasque.
const SOSIES = [21, 27, 33, 39, 49, 51, 57, 63, 69, 77, 81, 87, 91, 93, 99];
// Impairs non premiers de la variante « liste » du crible (multiples de 5 compris).
const COMPOSES_IMPAIRS = [9, 15, 21, 25, 27, 33, 35, 39, 45, 49, 51, 55, 57, 63,
    65, 69, 75, 77, 81, 85, 87, 91, 93, 95, 99];

// ═════════ Contrat des distracteurs (donnée pure, jamais lue à l'exécution) ═════════

export const REGLES_DISTRACTEURS_QCM = {
    nature_qcm_unique: {
        support: 'n tiré à 40 % parmi les premiers de 17 à 97, à 40 % parmi les sosies (21, 27, 33, 39, 49, 51, 57, 63, 69, 77, 81, 87, 91, 93, 99), à 10 % égal à 1, à 5 % égal à 2 et à 5 % pair de 4 à 98 ; trois choix fixes, la clé dépend du nombre de diviseurs de n',
        familles: {
            sosie_cru_premier: '« Oui » pour un sosie (51, 57, 91…) : impair qui ne finit pas par 5, jugé premier sans tester 3 ni 7',
            un_cru_premier: '« Oui » pour 1 : la remarque du cours (1 n\'a qu\'un seul diviseur) n\'est pas connue',
            premier_cru_non_premier: '« Non : il a un autre diviseur » pour un premier : un diviseur imaginé (2 jugé non premier parce que pair, 91 et 97 confondus…)',
            confusion_avec_un: '« Non : il n\'a qu\'un seul diviseur » pour n ≥ 2 : la phrase du cours sur 1 appliquée à un autre nombre',
            pair_cru_premier: '« Oui » pour un nombre pair autre que 2 : « 2 est le seul nombre pair qui est premier » n\'est pas mobilisé (ajout du correcteur : combinaison non nommée par les décisions)',
            un_raison_fausse: '« Non : il a un autre diviseur que 1 et lui-même » pour 1 : 1 est bien reconnu non premier, mais pour une raison fausse (ajout du correcteur : combinaison non nommée par les décisions)',
        },
        supprimees: {
            ni_premier_ni_compose: 'le choix « ni premier ni composé » reposait sur le mot « composé », absent du cours ; remplacé par la phrase du cours sur 1',
        },
        garantie: 'trois choix fixes et deux à deux distincts ; exactement un est juste par construction (nombre de diviseurs 1, 2 ou plus), revérifié par la définition du cours ; les deux autres portent chacun la famille de l\'erreur qu\'ils matérialisent pour ce n',
    },
    crible_qcm_multiple: {
        support: 'variante « liste » : k de 2 à 4 premiers de 17 à 97 et 6 − k impairs non premiers ; variante « intervalle » (un tirage sur deux) : les huit impairs de [a ; a + 20], a pair de 10 à 80, qui ne finissent pas par 5',
        familles: {
            multiple_de_3: 'impair non premier démasqué par le critère de 3 (21, 57, 87…)',
            multiple_de_5: 'impair qui finit par 5 (35, 65, 95), variante « liste » seulement',
            multiple_de_7: 'impair non premier qui échappe aux critères de 2, 3 et 5 : il faut la table de 7 (77, 91)',
            carre_impair: 'carré d\'un impair (9, 25, 49, 81), dont 49 = 7 × 7 qui a l\'air premier',
        },
        supprimees: {},
        garantie: 'chaque mauvais choix est non premier par construction (vivier d\'impairs non premiers, ou impair non premier de l\'intervalle), revérifié par la définition du cours ; le nombre annoncé dans la consigne est recompté sur les choix',
    },
};

// Famille d'un impair non premier : un carré d'abord, sinon le premier critère
// du cours qui le démasque (3, puis 5, puis 7), dans l'ordre du crible.
const familleNonPremier = (c) => {
    const r = Math.round(Math.sqrt(c));
    if (r * r === c) return 'carre_impair';
    if (c % 3 === 0) return 'multiple_de_3';
    if (c % 5 === 0) return 'multiple_de_5';
    if (c % 7 === 0) return 'multiple_de_7';
    throw new Error(`gen_153 : ${c} n'a pas de famille de non premier`);
};

// ═════════ Carte « nature » — n est-il premier ? ═════════

const NAT_OUI = 'Oui, c\'est un nombre premier.';
const NAT_AUTRE = 'Non : il a un autre diviseur que 1 et lui-même.';
const NAT_UN = 'Non : il n\'a qu\'un seul diviseur.';

// Tirage de n (décisions 2026-09-23) : 8/20 premier, 8/20 sosie, 2/20 le 1,
// 1/20 le 2, 1/20 un autre pair.
const tirerNature = () => {
    const r = randInt(1, 20);
    if (r <= 8) return { n: randomChoice(PREMIERS_17_97), categorie: 'premier' };
    if (r <= 16) return { n: randomChoice(SOSIES), categorie: 'sosie' };
    if (r <= 18) return { n: 1, categorie: 'un' };
    if (r === 19) return { n: 2, categorie: 'deux' };
    return { n: 2 * randInt(2, 49), categorie: 'pair' };
};

// Pour chaque catégorie : la clé, puis la famille de chacun des deux autres choix.
const FAMILLES_NATURE = {
    premier: { cle: NAT_OUI, [NAT_AUTRE]: 'premier_cru_non_premier', [NAT_UN]: 'confusion_avec_un' },
    deux: { cle: NAT_OUI, [NAT_AUTRE]: 'premier_cru_non_premier', [NAT_UN]: 'confusion_avec_un' },
    sosie: { cle: NAT_AUTRE, [NAT_OUI]: 'sosie_cru_premier', [NAT_UN]: 'confusion_avec_un' },
    pair: { cle: NAT_AUTRE, [NAT_OUI]: 'pair_cru_premier', [NAT_UN]: 'confusion_avec_un' },
    un: { cle: NAT_UN, [NAT_OUI]: 'un_cru_premier', [NAT_AUTRE]: 'un_raison_fausse' },
};

const genNatureQcm = () => {
    const { n, categorie } = tirerNature();
    const fam = FAMILLES_NATURE[categorie];
    // Second chemin : la clé se relit sur le nombre de diviseurs de n.
    const nd = nbDiviseurs(n);
    const cleDef = nd === 1 ? NAT_UN : nd === 2 ? NAT_OUI : NAT_AUTRE;
    if (cleDef !== fam.cle || (n > 1 && verifiePremier(n) !== (fam.cle === NAT_OUI))) {
        throw new Error(`gen_153 nature : clé incohérente pour ${n}`);
    }
    const autres = [NAT_OUI, NAT_AUTRE, NAT_UN].filter((c) => c !== fam.cle);
    const texte = `<p><b>Le nombre ${n} est-il un nombre premier ?</b></p>`;
    const payload = makeQcm([NAT_OUI, NAT_AUTRE, NAT_UN], fam.cle, texte, opts(L_NATURE));
    payload._meta.params = { n, categorie, distracteurs: autres.map((c) => fam[c]) };
    return payload;
};

const genNatureTableau = () => {
    // Quatre lignes (règle prof) : 1 à 3 premiers, le reste non premiers, donc
    // au moins une ligne vraie et une fausse, jamais de doublon.
    const k = randInt(1, 3);
    const premiers = randomChoiceMultiple(PREMIERS_17_97, k);
    const nonPremiers = randomChoiceMultiple([...SOSIES, 1], 4 - k);
    const nombres = shuffle([...premiers, ...nonPremiers]);
    const lignes = nombres.map((n) => `${n} est un nombre premier.`);
    const bonnes = nombres.map((n) => (n > 1 && verifiePremier(n) ? 0 : 1)); // 0 = Vrai, 1 = Faux
    if (bonnes.filter((b) => b === 0).length !== k) throw new Error('gen_153 tableau : nombre de premiers incohérent');
    const payload = makeVraiFaux(lignes, bonnes,
        '<p><b>Vrai ou faux ?</b> Pour chaque nombre, dis s\'il est premier.</p>',
        opts(L_NATURE));
    payload._meta.params = { nombres };
    return payload;
};

// ═════════ Carte « crible » — Sélectionne les nombres premiers (QCM multiple) ═════════

const genCribleListe = () => {
    const k = randInt(2, 4);
    const premiers = randomChoiceMultiple(PREMIERS_17_97, k);
    const nonPremiers = randomChoiceMultiple(COMPOSES_IMPAIRS, 6 - k);
    return { k, premiers, nonPremiers, texte: `<p><b>Sélectionne les ${k} nombres premiers de cette liste.</b></p>`, variante: 'liste' };
};

const genCribleIntervalle = () => {
    // a pair : [a ; a + 20] contient dix impairs, dont exactement deux finissent
    // par 5 ; il reste huit choix (lettres A à H). Bornes paires, donc jamais
    // premières : « compris entre » n'a rien à trancher.
    const a = 2 * randInt(5, 40);
    const b = a + 20;
    const candidats = [];
    for (let x = a + 1; x < b; x += 2) if (x % 10 !== 5) candidats.push(x);
    const premiers = candidats.filter((x) => verifiePremier(x));
    const nonPremiers = candidats.filter((x) => !premiers.includes(x));
    const k = premiers.length;
    return { k, premiers, nonPremiers, a, b, texte: `<p><b>Sélectionne les ${k} nombres premiers compris entre ${a} et ${b}.</b></p>`, variante: 'intervalle' };
};

const genCribleQcmMultiple = () => {
    const t = randInt(0, 1) === 0 ? genCribleListe() : genCribleIntervalle();
    // Second chemin : chaque choix revérifié par la définition du cours.
    if (!t.premiers.every(verifiePremier) || t.nonPremiers.some(verifiePremier)
        || t.premiers.length !== t.k || t.premiers.length + t.nonPremiers.length > 8) {
        throw new Error('gen_153 crible : choix incohérents');
    }
    const choix = shuffle([...t.premiers, ...t.nonPremiers].map(String));
    const payload = makeQcm(choix, t.premiers.map(String), t.texte, opts(L_CRIBLE), true);
    payload._meta.params = {
        variante: t.variante, premiers: t.premiers, nonPremiers: t.nonPremiers,
        ...(t.variante === 'intervalle' ? { a: t.a, b: t.b } : {}),
        distracteurs: t.nonPremiers.map(familleNonPremier),
    };
    return payload;
};

// ═════════ Carte « liste » — La liste des premiers < 100 ═════════

const I_ONZE = PREMIERS_100.indexOf(11);

const genListeTrous = () => {
    const L = randInt(5, 7);
    const start = randInt(I_ONZE, PREMIERS_100.length - L);
    const fenetre = PREMIERS_100.slice(start, start + L);
    // Second chemin : fenêtre de premiers CONSÉCUTIFS (aucun premier sauté).
    for (let x = fenetre[0]; x <= fenetre[L - 1]; x++) {
        if ((nbDiviseurs(x) === 2) !== fenetre.includes(x)) throw new Error('gen_153 liste : fenêtre non consécutive');
    }
    // On cache 2 ou 3 valeurs, jamais la première (elle sert d'ancre)
    const nbTrous = randInt(2, 3);
    const positionsCachables = shuffle(fenetre.map((_, i) => i).slice(1));
    const caches = new Set(positionsCachables.slice(0, nbTrous));
    const phrase = fenetre
        .map((p, i) => (caches.has(i) ? `{{${p}}}` : String(p)))
        .join(' ; ');
    const payload = makeTexteTrou(phrase,
        '<p><b>Complète la liste des nombres premiers.</b></p>'
        + '<p>Ils sont rangés dans l\'ordre croissant, sans en oublier aucun.</p>',
        { ...opts(L_LISTE), mode: 'saisie' });
    payload._meta.params = { fenetre, caches: [...caches] };
    return payload;
};

// Intervalles à bornes multiples de 10 (jamais premières), largeurs 10, 20 ou
// 30 : 24 énoncés distincts.
const PAIRES_INTERVALLES = (() => {
    const paires = [];
    for (const w of [10, 20, 30]) {
        for (let a = 10; a + w <= 100; a += 10) paires.push([a, a + w]);
    }
    return paires;
})();

const genListeCompter = () => {
    const [a, b] = randomChoice(PAIRES_INTERVALLES);
    const count = PREMIERS_100.filter((p) => p > a && p < b).length;
    let parDefinition = 0;
    for (let x = a; x <= b; x++) if (nbDiviseurs(x) === 2) parDefinition++;
    if (parDefinition !== count) throw new Error('gen_153 liste : comptage incohérent');
    const texte = `<p><b>Combien y a-t-il de nombres premiers compris entre ${a} et ${b} ?</b></p>`;
    const payload = makeSaisie(String(count), texte, opts(L_LISTE));
    payload._meta.params = { a, b, count };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    nature_qcm_unique: genNatureQcm,
    nature_tableau_vf: genNatureTableau,
    crible_qcm_multiple: genCribleQcmMultiple,
    liste_texte_trou_saisie: genListeTrous,
    liste_texte: genListeCompter,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux', qcm_multiple: 'QCM Multi',
    texte: 'Saisie', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'nature', label: L_NATURE, formatsList: buildFormats('nature') },
    { id: 'crible', label: L_CRIBLE, formatsList: buildFormats('crible') },
    { id: 'liste', label: L_LISTE, formatsList: buildFormats('liste') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
